import React, { useMemo } from 'react';

/** Alturas por size para a logo em imagem (proporção horizontal) */
const LOGO_IMAGE_SIZES = {
  xs: { height: 20, width: 60 },
  sm: { height: 28, width: 84 },
  default: { height: 36, width: 108 },
  md: { height: 44, width: 132 },
  lg: { height: 52, width: 156 },
  xl: { height: 64, width: 192 },
};

/**
 * Componente de Logo Ufokko
 *
 * Usa a logo oficial em PNG (identidade visual). variant light = logo azul (fundo claro),
 * dark = logo para fundo escuro (azul claro/branco). Fallback: texto + ícone SVG.
 */
const UfokkoLogo = ({
  size = 'default',
  color,
  variant,
  className = '',
  showText = true,
  useImageLogo = true,
}) => {
  const effectiveVariant = variant ?? (color === 'dark' ? 'dark' : 'light');
  const logoImgSize = LOGO_IMAGE_SIZES[size] || LOGO_IMAGE_SIZES.default;

  // Logo oficial em PNG (identidade Ufokko). Para tema escuro pode usar /logo/ufokko-logo-branco.png quando existir.
  const logoSrc = '/logo/ufokko-logo-azul.png';

  if (useImageLogo) {
    return (
      <img
        src={logoSrc}
        alt="Ufokko"
        className={`object-contain object-left ${className}`}
        style={{
          height: logoImgSize.height,
          width: 'auto',
          maxWidth: logoImgSize.width,
        }}
        width={logoImgSize.width}
        height={logoImgSize.height}
      />
    );
  }

  // Fallback: logo em texto + SVG (antigo)
  const effectiveColor = color ?? (effectiveVariant === 'light' ? 'primary' : 'default');
  const gradientId = useMemo(() => `ufokko-gradient-${Math.random().toString(36).substr(2, 9)}`, []);
  const sizes = {
    xs: { text: 'text-xs', icon: 'w-3 h-3' },
    sm: { text: 'text-sm', icon: 'w-4 h-4' },
    default: { text: 'text-2xl', icon: 'w-6 h-6' },
    md: { text: 'text-3xl', icon: 'w-8 h-8' },
    lg: { text: 'text-4xl', icon: 'w-10 h-10' },
    xl: { text: 'text-5xl', icon: 'w-12 h-12' },
  };
  const colors = {
    default: 'text-white',
    dark: 'text-gray-900',
    light: 'text-gray-100',
    gradient: 'bg-gradient-to-r from-[#0E76FF] to-[#2563EB] bg-clip-text text-transparent',
    blue: 'text-primary',
    primary: 'text-primary',
  };
  const sizeConfig = sizes[size] || sizes.default;
  const colorClass = colors[effectiveColor] || colors.default;

  const StyledO = ({ gradientId }) => {
    const isDark = effectiveColor === 'dark';
    const isGradient = effectiveColor === 'gradient';
    const fillColor = isDark ? '#000000' : (isGradient ? '#0E76FF' : 'currentColor');
    const strokeColor = isDark ? '#000000' : (isGradient ? '#0E76FF' : 'currentColor');
    const centerDotColor = isDark ? '#ffffff' : (isGradient ? '#ffffff' : (effectiveColor === 'default' ? '#ffffff' : '#000000'));
    const iconSize = size === 'xs' ? 12 : size === 'sm' ? 16 : size === 'default' ? 24 : size === 'md' ? 32 : size === 'lg' ? 40 : 48;
    return (
      <span className="relative inline-block" style={{ lineHeight: 1, verticalAlign: 'middle' }}>
        <svg width={iconSize} height={iconSize} viewBox="0 0 24 24" className="inline-block" style={{ verticalAlign: 'middle', display: 'inline-block' }}>
          <defs>
            {isGradient && (
              <linearGradient id={gradientId} x1="0%" y1="0%" x2="100%" y2="0%">
                <stop offset="0%" stopColor="#0E76FF" />
                <stop offset="100%" stopColor="#2563EB" />
              </linearGradient>
            )}
          </defs>
          <circle cx="12" cy="12" r="10" fill="none" stroke={isGradient ? `url(#${gradientId})` : strokeColor} strokeWidth="2" />
          <circle cx="12" cy="12" r="6" fill={isGradient ? `url(#${gradientId})` : fillColor} />
          <circle cx="12" cy="12" r="2" fill={centerDotColor} />
        </svg>
      </span>
    );
  };

  if (!showText) return <StyledO gradientId={gradientId} />;
  return (
    <span className={`font-bold ${sizeConfig.text} ${colorClass} flex items-center gap-1 ${className}`} style={{ fontFamily: 'system-ui, -apple-system, sans-serif' }}>
      <span className="font-normal tracking-tight">uf</span>
      <StyledO gradientId={gradientId} />
      <span className="font-normal tracking-tight">kko</span>
    </span>
  );
};

// Versão SVG otimizada para uso em tamanhos pequenos
export const UfokkoLogoSVG = ({ width = 120, height = 40, className = '' }) => {
  return (
    <svg 
      width={width} 
      height={height} 
      viewBox="0 0 120 40" 
      className={className}
      xmlns="http://www.w3.org/2000/svg"
    >
      {/* Texto "uf" */}
      <text 
        x="5" 
        y="28" 
        fontSize="24" 
        fontWeight="400" 
        fill="white" 
        fontFamily="system-ui, -apple-system, sans-serif"
        letterSpacing="1"
      >
        uf
      </text>
      
      {/* "o" estilizado (alvo) */}
      <g transform="translate(35, 8)">
        {/* Círculo externo */}
        <circle cx="12" cy="12" r="10" fill="none" stroke="white" strokeWidth="2" />
        {/* Círculo interno */}
        <circle cx="12" cy="12" r="6" fill="white" />
        {/* Ponto central */}
        <circle cx="12" cy="12" r="2" fill="black" />
      </g>
      
      {/* Texto "kko" */}
      <text 
        x="60" 
        y="28" 
        fontSize="24" 
        fontWeight="400" 
        fill="white" 
        fontFamily="system-ui, -apple-system, sans-serif"
        letterSpacing="1"
      >
        kko
      </text>
    </svg>
  );
};

// Versão React com SVG inline (melhor para responsividade)
export const UfokkoLogoInline = ({ size = 24, className = '', color = 'white' }) => {
  const letterSize = size * 0.8;
  const iconSize = size;
  
  return (
    <span 
      className={`inline-flex items-center gap-1 ${className}`}
      style={{ fontSize: `${letterSize}px`, fontFamily: 'system-ui, -apple-system, sans-serif' }}
    >
      <span style={{ fontWeight: 400, color }}>uf</span>
      <svg 
        width={iconSize} 
        height={iconSize} 
        viewBox="0 0 24 24" 
        className="inline-block"
        style={{ margin: '0 2px' }}
      >
        {/* Círculo externo */}
        <circle 
          cx="12" 
          cy="12" 
          r="10" 
          fill="none" 
          stroke={color} 
          strokeWidth="2" 
        />
        {/* Círculo interno */}
        <circle 
          cx="12" 
          cy="12" 
          r="6" 
          fill={color} 
        />
        {/* Ponto central (invertido) */}
        <circle 
          cx="12" 
          cy="12" 
          r="2" 
          fill={color === 'white' ? 'black' : 'white'} 
        />
      </svg>
      <span style={{ fontWeight: 400, color }}>kko</span>
    </span>
  );
};

export default UfokkoLogo;

